/* =======================================================
   OGATU — HERO SLIDESHOW — SYSTEM BUILD (GLOBAL REVEAL)
   File: assets/section-ogatu-hero-slideshow.js
   ======================================================= */

(function () {
  const SELECTOR = '[data-ogatu-hero]';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setupMuteButtons(root) {
    const buttons = root.querySelectorAll('[data-ogatu-mute]');
    buttons.forEach((btn) => {
      if (btn.__ogatuBound) return;
      btn.__ogatuBound = true;

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const slide = btn.closest('.ogatu-hero__slide');
        const video = slide ? slide.querySelector('video') : null;
        if (!video) return;

        const willUnmute = video.muted === true;
        video.muted = !willUnmute;

        if (willUnmute) {
          const p = video.play?.();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }

        btn.setAttribute('aria-pressed', willUnmute ? 'true' : 'false');
        btn.setAttribute('aria-label', willUnmute ? 'Mute video' : 'Unmute video');
      });
    });
  }

  function triggerGlobalReveal(activeSlide) {
    // Uses the global system only (ogatu-motion.js exposes this)
    if (!activeSlide) return;
    if (window.OgatuMotion && typeof window.OgatuMotion.reveal === 'function') {
      window.OgatuMotion.reveal(activeSlide);
    } else {
      // Fallback: ensure content is visible even if motion script is missing
      activeSlide.querySelectorAll('[data-ogatu-reveal]').forEach((el) => {
        el.classList.add('ogatu-inview');
      });
    }
  }

  class OgatuHeroSlideshow {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-track]');
      this.slides = Array.from(root.querySelectorAll('[data-slide]'));
      this.navButtons = Array.from(root.querySelectorAll('.ogatu-hero__dot, .ogatu-hero__num'));
      this.prevBtn = root.querySelector('[data-arrow="prev"]');
      this.nextBtn = root.querySelector('[data-arrow="next"]');

      this.reduced = prefersReducedMotion();

      this.transition = (root.dataset.transition || 'fade').toLowerCase();
      this.transitionMs = parseInt(root.dataset.transitionMs || '900', 10);

      this.enableAutoplay = root.dataset.autoplay === 'true' && !this.reduced;
      this.autoplayMs = parseInt(root.dataset.autoplayMs || '7000', 10);
      this.pauseOnHover = root.dataset.pauseOnHover === 'true';

      this.active = 0;
      this.timer = null;
      this.isPaused = false;

      this._touch = { x0: 0, y0: 0, active: false };
      this._prevTimer = null;

      setupMuteButtons(root);
      this._applyTransitionMode();
      this._bind();
      this.setActive(0, false);
      this.startAutoplay();
    }

    _applyTransitionMode() {
      const isSlide = this.transition === 'slide';
      this.root.classList.toggle('is-slide', isSlide);
      this.root.style.setProperty('--ogatu-transition-ms', `${this.transitionMs}ms`);
      if (isSlide) this._applyTrackTransform();
    }

    _applyTrackTransform() {
      if (!this.root.classList.contains('is-slide')) return;
      if (!this.track) return;
      this.track.style.transform = `translate3d(-${this.active * 100}%, 0, 0)`;
    }

    _syncThemeFromActiveSlide() {
      const slide = this.slides[this.active];
      const theme = (slide?.dataset?.theme || slide?.getAttribute('data-theme') || 'light').toLowerCase();
      this.root.classList.toggle('is-theme-light', theme === 'light');
      this.root.classList.toggle('is-theme-dark', theme === 'dark');
    }

    _pauseNonActiveVideos() {
      this.slides.forEach((slide, idx) => {
        const vids = slide.querySelectorAll('video');
        vids.forEach((v) => {
          if (idx !== this.active) {
            try { v.pause(); } catch (e) {}
          } else {
            const p = v.play?.();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }
        });
      });
    }

    _clearPrevState() {
      if (this._prevTimer) window.clearTimeout(this._prevTimer);
      this._prevTimer = null;
      this.slides.forEach((s) => s.classList.remove('is-prev'));
    }

    _bind() {
      if (!this.slides.length) return;

      this.navButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.dotIndex, 10);
          if (!Number.isNaN(idx)) this.setActive(idx, true);
        });
      });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', () => (this.isPaused = true));
        this.root.addEventListener('mouseleave', () => (this.isPaused = false));
        this.root.addEventListener('focusin', () => (this.isPaused = true));
        this.root.addEventListener('focusout', () => (this.isPaused = false));
      }

      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      });

      // Swipe
      this.root.addEventListener('touchstart', (e) => {
        const t = e.touches && e.touches[0];
        if (!t) return;
        this._touch.x0 = t.clientX;
        this._touch.y0 = t.clientY;
        this._touch.active = true;
      }, { passive: true });

      this.root.addEventListener('touchmove', (e) => {
        if (!this._touch.active) return;
        const t = e.touches && e.touches[0];
        if (!t) return;

        const dx = t.clientX - this._touch.x0;
        const dy = t.clientY - this._touch.y0;
        if (Math.abs(dy) > Math.abs(dx)) return;

        if (Math.abs(dx) > 18) e.preventDefault();
      }, { passive: false });

      this.root.addEventListener('touchend', (e) => {
        if (!this._touch.active) return;
        this._touch.active = false;

        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;

        const dx = t.clientX - this._touch.x0;
        if (Math.abs(dx) < 40) return;

        if (dx < 0) this.next();
        else this.prev();
      });
    }

    setActive(index, userTriggered) {
      if (!this.slides.length) return;

      const next = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      const was = this.active;
      this.active = next;

      if (this.root.classList.contains('is-slide')) {
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === next));
        this._applyTrackTransform();
      } else {
        this._clearPrevState();
        const prevSlide = this.slides[was];
        if (prevSlide) prevSlide.classList.add('is-prev');

        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === next));

        this._prevTimer = window.setTimeout(() => {
          this._clearPrevState();
        }, Math.max(0, this.transitionMs + 40));
      }

      this.navButtons.forEach((btn) => {
        const idx = parseInt(btn.dataset.dotIndex, 10);
        btn.classList.toggle('is-active', idx === next);
      });

      this._syncThemeFromActiveSlide();
      this._pauseNonActiveVideos();

      // ✅ Trigger global reveal on the active slide every time
      const activeSlide = this.slides[this.active];
      triggerGlobalReveal(activeSlide);

      if (userTriggered) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    next() { this.setActive(this.active + 1, true); }
    prev() { this.setActive(this.active - 1, true); }

    startAutoplay() {
      if (!this.enableAutoplay || this.slides.length < 2) return;
      this.stopAutoplay();

      this.timer = window.setInterval(() => {
        if (this.isPaused) return;
        this.setActive(this.active + 1, false);
      }, Math.max(1500, this.autoplayMs));
    }

    stopAutoplay() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  function mountAll() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.__ogatuHero) return;
      root.__ogatuHero = new OgatuHeroSlideshow(root);
    });
  }

  function remountOnSectionEvents(e) {
    const root = e.target?.querySelector?.(SELECTOR);
    if (!root) return;

    if (root.__ogatuHero) {
      root.__ogatuHero.stopAutoplay();
      delete root.__ogatuHero;
    }
    root.__ogatuHero = new OgatuHeroSlideshow(root);
  }

  document.addEventListener('DOMContentLoaded', mountAll);
  document.addEventListener('shopify:section:load', remountOnSectionEvents);
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target?.querySelector?.(SELECTOR);
    if (root?.__ogatuHero) {
      root.__ogatuHero.stopAutoplay();
      delete root.__ogatuHero;
    }
  });
})();

/* =======================================================
   FULL-SLIDE CLICK (navigate to cta_link)
   ======================================================= */
document.addEventListener('click', (e) => {
  const slide = e.target.closest('[data-slide]');
  if (!slide) return;

  const fullLink = slide.querySelector('.ogatu-hero__full-link');
  if (!fullLink || !fullLink.href) return;

  if (
    e.target.closest('a') ||
    e.target.closest('button') ||
    e.target.closest('[data-arrow]') ||
    e.target.closest('[data-dot-index]') ||
    e.target.closest('[data-ogatu-mute]')
  ) return;

  window.location.href = fullLink.href;
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' && e.key !== ' ') return;

  const slide = document.activeElement?.closest?.('[data-slide]');
  if (!slide) return;

  const fullLink = slide.querySelector('.ogatu-hero__full-link');
  if (!fullLink || !fullLink.href) return;

  e.preventDefault();
  window.location.href = fullLink.href;
});